import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Legend from '../../components/Legend';
import {IDashboardData, IFdcDashboardResult, SurveySlug} from '../../utils/types';
import {FDC_LABELS} from '../labels';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
    surveyEvalId: string;
    surveyPublicId: string;
}

const buildApiKey = (slug: SurveySlug): string => (slug.denominator ? `${slug.numerator.question}_${slug.denominator.question}` : slug.numerator.question);

const buildSlugs = (surveyId: number, surveyEvalId: number, surveyPublicId: number): SurveySlug[] => [
    {
        numerator: {survey_id: surveyPublicId, question: 'domanda17'},
        denominator: {survey_id: surveyEvalId, question: 'question2'},
        operation: 'sum',
        key: 'i13',
        roman: 'XIII',
        title: 'Reach della formazione diretta a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di enti coinvolti in corsi o eventi di formazione organizzati direttamente dalla Fondazione di Comunità durante l'anno e il numero complessivo di enti ammissibili del territorio.",
    },
    {
        numerator: {survey_id: surveyPublicId, question: 'domanda16'},
        denominator: {survey_id: surveyId, question: 'question12'},
        operation: 'sum',
        key: 'i14',
        roman: 'XIV',
        title: 'Leva del cofinanziamento a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra l'ammontare del cofinanziamento complessivo apportato dagli enti sostenuti e il valore complessivo dei contributi deliberati durante l'anno.",
    },
    {
        numerator: {survey_id: surveyPublicId, question: 'domanda18'},
        denominator: {survey_id: surveyId, question: 'question8'},
        operation: 'sum',
        key: 'i15',
        roman: 'XV',
        title: 'Tasso di accompagnamento delle candidature a confronto con la media',
        tooltip:
            "L'indicatore mostra il rapporto fra il numero di enti incontrati in fase di candidatura a bandi nel corso dell'anno e il numero complessivo di enti richiedenti nell'anno.",
    },
];

const Dashboard3 = ({container, uoaId, surveyId, surveyEvalId, surveyPublicId}: Props) => {
    const slugs = buildSlugs(Number(surveyId), Number(surveyEvalId), Number(surveyPublicId));

    const {data, isLoading, isError} = useQuery<IFdcDashboardResult>({
        queryKey: ['fdc-dashboard3', container, uoaId, surveyId, surveyEvalId, surveyPublicId],
        queryFn: async () => {
            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    with_groups: 1,
                    question_slugs: slugs.map((s) => ({
                        numerator: s.numerator,
                        denominator: s.denominator,
                        operation: s.operation,
                    })),
                },
            });

            const groupNumber: number | null = res.data['__group_number'] ?? null;
            const indicators: IDashboardData = {};
            for (const slug of slugs) {
                const apiKey = buildApiKey(slug);
                indicators[slug.key] = res.data[apiKey] ?? {uoa: null, average: null, group: null};
            }
            return {indicators, groupNumber};
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Legend labels={FDC_LABELS} groupNumber={data.groupNumber} />

            {slugs.map((slug) => (
                <Indicator
                    key={slug.key}
                    roman={slug.roman}
                    title={slug.title}
                    tooltip={slug.tooltip}
                    data={data.indicators[slug.key]}
                    isCount={true}
                    labels={FDC_LABELS}
                />
            ))}
        </Box>
    );
};

export default Dashboard3;
